import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {FaCheckCircle, FaExclamationCircle, FaPlus} from "react-icons/fa";
import {
  FaEye,
  FaPencilAlt,
  FaExclamationTriangle,
  FaTrashAlt,
} from "react-icons/fa";
import { FaDownload } from "react-icons/fa";
import { Button } from "../../components/ui/button";
import Modal from "../../components/Modal";
import { Label } from "../../components/ui/label";
import { DataTable } from "../../components/DataTable";
import ProfileCard from "../../components/ProfileCard";
import { Input } from "../../components/ui/input";
import {
  communityService,
  dashboardService,
  groupService,
  invitationService,
  membershipService, membershipDataMap,
  ServiceOnMount, mapMembershipRole,
} from "../../services";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import useUserStore from "../../stores/userStore";
import ShortenedText from "../../components/ShortenedText";
import TableCard from "../../components/TableCard";
import { createPageStore } from "../../stores/pages/pageStore";
import Papa from "papaparse";
import { GoLog, GoPeople } from "react-icons/go";
import MultiEmailSelect from "../../components/ChangeSelect";
import { toast } from "react-toastify";
import LoadingImage from "../../components/LoadingImage";
import dayjs from "dayjs";
import useUserStore2 from "../../stores/userStore2";
import { Skeleton } from "../../components/ui/skeleton";
import { createItemStore } from "../../stores/createItemStore"
import StatusBadge from "../../components/table/StatusBadge"

const columns = [
  // {
  //   accessorKey: "memberId",
  //   header: "Member ID",
  //   cell: ({ row }) => (
  //     <span>
  //       <ShortenedText text={row.getValue("memberId")} />
  //     </span>
  //   ),
  // },
  {
    accessorKey: "profileName",
    header: "Profile Name",
    cell: ({ row }) => <span>{row.getValue("profileName")}</span>,
  },
  {
    accessorKey: "email",
    header: "Email Address",
    cell: ({ row }) => <span>{row.getValue("email")}</span>,
  },
  {
    accessorKey: "groupName",
    header: "Group",
    cell: ({ row }) => <span>{row.getValue("groupName")}</span>,
  },
  {
    accessorKey: "role",
    header: "Role",
    cell: ({ row }) => <span>{mapMembershipRole[row.getValue("role")]}</span>,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status");
      return (
        <StatusBadge
          status={status === "Deactivated" && !row.original.verified
            ? "Invitation sent"
            : status}
        />
      );
    },
  },
  {
    accessorKey: "lastLogin",
    header: "Last Login",
    cell: ({row}) => (
      <span>
        {
          row.getValue("lastLogin")
            ? dayjs(row.getValue("lastLogin")).fromNow()
            : "never"

          // new Date(row.getValue("lastLogin")).toLocaleString()
        }
      </span>
    ),
  },
];

/**
 * @typedef {Object} MemberItem
 * @property {string} membershipId - Unique identifier for the membership
 * @property {string} communityId - ID of the community the member belongs to
 * @property {string} memberId - Unique identifier for the member
 * @property {string} memberName - Name of the member
 * @property {string} email - Email address of the member
 * @property {string} status - Current status of the member (e.g., "Active", "Suspended", "Pending")
 * @property {string} lastLogin - Timestamp of the member's last login
 * @property {string} dateCreated - Date when the membership was created
 * @property {string} lastUpdate - Timestamp of the last update to the membership
 */

/**
 * @typedef {Object} MembersTable
 * @property {MemberItem[]} membersTable
 */

/** @type {MembersTable} */
// const initialState = {
//   membersTable: [],
// };
// const useMembersStore = createPageStore("members", initialState);


// ========================================================
// Step 1: Zustand store instantiation
// ========================================================
const storeName = "memberships";
const useItems = createItemStore(
  storeName,
  (memberships) => memberships.membershipId,
  (args) => membershipService.getMembershipByCommunity(args[0]),
  membershipDataMap
);

// ========================================================


const MemberManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [communityName, setCommunityName] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isActivateOpen, setIsActivateOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [file, setFile] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { communityId } = useParams();
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [memberName, setMemberName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState([]);
  const { user } = useUserStore2();
  const [pendingRequests, setPendingRequests] = useState({
    total: 0,
    oldTotal: 0,
  });
  const [isPendingRequestsLoading, setIsPendingRequestsLoading] = useState(true);

  const [totalMembers, setTotalMembers] = useState({
    total: 0,
    oldTotal: 0,
  });
  const [isTotalMembersLoading, setIsTotalMembersLoading] = useState(true);


  /**
   * @typedef {Object} MembersTableStore
   * @property {MembersTable} page - Array of member items in the table
   * @property {function} setPage - Function to update the members table data
   */

  /** @type {MembersTableStore} */
  // const { page, setPage } = useMembersStore();

  // Modify handleFileImport
  const handleFileImport = () => {
    if (!file) {
      alert("Please select a file to import.");
      return;
    }

    setLoading(true);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const csvData = results.data;

          // Validate and map CSV data
          const invitationList = csvData.map((row) => {
            // Find group ID by name (case insensitive)
            const groupName = row.Group?.trim() || row.GroupName?.trim();
            let groupId = "";

            if (groupName) {
              const matchedGroup = getGroups.data?.find(
                (g) => g.groupName.toLowerCase() === groupName.toLowerCase()
              );
              groupId = matchedGroup?.groupId || "";
            }

            return {
              Email: row.Email?.trim(),
              Name: row.Name?.trim(),
              Role: row.Role?.trim() || "U", // Default role
              CommunityId: communityId,
              GroupId: groupId,
              GroupName: groupName, // Keep the original name for error reporting
            };
          });

          // 1. First check for missing emails or names
          const invalidRows = invitationList.filter(
            (user) => !user.Email || !user.Name || !user.Role || !user.GroupName
          );

          if (invalidRows.length > 0) {
            alert("Some rows are missing required fields.");
            return;
          }

          // 2. NEW VALIDATION: Check for invalid group names
          const groupsWithInvalidNames = invitationList.filter(
            (user) => user.GroupName && !user.GroupId
          );

          if (groupsWithInvalidNames.length > 0) {
            const invalidNames = [
              ...new Set(groupsWithInvalidNames.map((u) => u.GroupName)),
            ];
            toast.error(
              `The following group names were not found: ${invalidNames.join(
                ", "
              )}`,
              { autoClose: 5000 }
            );
            return;
          }

          setLoading(true);

          // Call the service
          await invitationService.bulkInvite({
            communityId,
            invitationList: invitationList.map(
              ({ GroupName, ...rest }) => rest
            ), // Remove GroupName before sending
          });

          toast.success("🎉 Users imported successfully!", {
            autoClose: 2000,
          });
          setShowImportModal(false);
          setFile(null);
          setReload((prev) => !prev);
        } catch (error) {
          toast.error("❌ Failed to import users, please try again", {
            autoClose: 2000,
          });
          console.error(error);
        } finally {
          setLoading(false);
        }
      },
      error: (error) => {
        toast.error("❌ Error parsing csv file", {
          autoClose: 2000,
        });
        console.error("CSV Parse Error:", error);
        setLoading(false);
      },
    });
  };

  const handleConfirmDelete = async () => {
    if (!selectedMember?.membershipId) {
      console.error("No membership ID found for deletion.");
      return;
    }

    try {
      setIsDeleting(true); // Optional: Set a loading state if needed
      await membershipService.removeMembership(selectedMember.membershipId);
      console.log("Member successfully removed:", selectedMember.membershipId);

      setShowDeleteModal(false);
      setReload((prev) => !prev);
    } catch (error) {
      console.error("Error removing membership:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const actions = (row) => {
    const status = row.status;
    const baseActions = [
      {
        label: "View Details",
        icon: <FaEye/>,
        onClick: () => {
          setSelectedMember(row); // Pass the row data as the selected member
          setShowViewModal(true); // Open the modal
        },
        variant: "dropPlain",
      },

      {
        label: "Edit Details",
        icon: <FaPencilAlt/>,
        onClick: () => {
          setSelectedMember(row); // Pass the row data as the selected member
          setShowEditModal(true); // Open the modal
        },
        variant: "dropPlain",
      },
      {
        label: "Activate Member",
        icon: <FaExclamationTriangle/>,
        onClick: () => {
          setSelectedMember(row);
          setIsActivateOpen(true);
        },
        variant: "dropGreen",
        hidden: status === "Active",
      },
      {
        label: "Suspend Member",
        icon: <FaExclamationCircle/>,
        onClick: () => {
          setSelectedMember(row);
          setIsOpen(true);
        },
        variant: "dropRed",
        hidden: status === "Suspended" || status === "Deactivated",
      },
      {
        label: "Remove Member",
        icon: <FaTrashAlt/>,
        onClick: () => {
          setSelectedMember(row);
          setShowDeleteModal(true);
        },
        variant: "dropRed",
      },
    ]
    return baseActions.filter((action) => !action.hidden);
  };

  // const statusMapping = {
  //   A: "Active",
  //   S: "Suspended",
  //   default: "Pending", // Fallback
  // };

  const roleMapping = {
    U: "User",
    GL: "Group Leader",
    CA: "Community Admin",
  };

  // const dataMap = (item) => ({
  //   membershipId: item.membershipId,
  //   communityId: item.communityId,
  //   memberId: item.profileId,
  //   memberName: item.profileName,
  //   email: item.email,
  //   status: statusMapping[item.status] || statusMapping.default,
  //   lastLogin: item?.lastLogin,
  //   dateCreated: item.profileCreateDate
  //     ? new Date(item.profileCreateDate).toLocaleDateString()
  //     : "N/A",
  //   lastUpdate: item.lastUpdate
  //     ? new Date(item.lastUpdate).toLocaleString()
  //     : "N/A",
  //   group: item.groupName,
  //   role: item.role,
  // });

  const [reload, setReload] = useState(false);

  // const getCommunityMembership = ServiceOnMount(
  //   () => membershipService.getMembershipByCommunity(selectedId),
  //   dataMap,
  //   [reload]
  // );

  // console.log("getcommunitymembership data: ", getCommunityMembership);

  // if (getCommunityMembership?.data)
  //   setPage((state) => {
  //     state.membersTable = getCommunityMembership.data;
  //   });

  const groupMap = (item) => ({
    communityId: item.community._id,
    groupId: item._id,
    groupName: item.name,
    status: item.status,
  });

  const getGroups = ServiceOnMount(
    () => groupService.getGroups(communityId),
    groupMap,
    [reload]
  );

  const handleStatusChange = (status) => {
    setSelectedMember((prevState) => ({
      ...prevState,
      status: status,
    }));
  };

  const handleSaveChanges = async () => {
    if (!selectedMember) return;

    const statusCode = selectedMember.status === "Active" ? "A" : "S"; // "A" for Active, "U" for Suspended

    const payload = {
      MembershipId: selectedMember.membershipId,
      CommunityId: selectedMember.communityId,
      ProfileId: selectedMember.profileId,
      Status: statusCode,
    };
    console.log("Payload:", payload);
    console.log("Selected Member:", selectedMember);

    try {
      setIsLoading(true); // Start loading
      await membershipService.editMembershipDetails(payload);
      setShowEditModal(false);
      setReload((prev) => !prev); // Refresh the list
    } catch (error) {
      console.error("Failed to update membership:", error);
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  const handleConfirmActivate = async () => {
    if (!selectedMember) return;

    const payload = {
      MembershipId: selectedMember.membershipId,
      CommunityId: selectedMember.communityId,
      ProfileId: selectedMember.profileId,
      Status: "A",
    };

    try {
      setIsLoading(true); // Start loading
      await membershipService.editMembershipDetails(payload);
      setIsActivateOpen(false);
      setReload((prev) => !prev); // Refresh the membership list
    } catch (error) {
      console.error("Failed to activate membership:", error);
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  const handleConfirmSuspend = async () => {
    if (!selectedMember) return;

    const payload = {
      MembershipId: selectedMember.membershipId,
      CommunityId: selectedMember.communityId,
      ProfileId: selectedMember.profileId,
      Status: "S",
    };

    try {
      setIsLoading(true); // Start loading
      await membershipService.editMembershipDetails(payload);
      setIsOpen(false);
      setReload((prev) => !prev); // Refresh the membership list
    } catch (error) {
      console.error("Failed to suspend membership:", error);
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  const handleInviteMember = async () => {
    if (!memberName || !email || !selectedRole) {
      setError("All fields are required.");
      return;
    }

    setLoading(true);
    setError(null);

    const inviteData = {
      Email: email[0],
      Role: selectedRole,
      Name: memberName,
      CommunityId: communityId, // Get from Zustand store
      GroupId: selectedGroup || null, // Only include if selected
    };

    try {
      const response = await invitationService.sendInvite(inviteData);
      console.log("Invite Sent Successfully:", response);
      setShowAddModal(false); // Close modal on success
    } catch (error) {
      setError("Failed to send invite. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (newEmails) => {
    // Set the emails when they change
    setEmail(newEmails);
  };

  // Update the pending requests useEffect
  useEffect(() => {
    const fetchPendingRequests = async () => {
      if (!user?.id) {
        setIsPendingRequestsLoading(true);
        return;
      }

      try {
        setIsPendingRequestsLoading(true);
        const response = await dashboardService.countTotalPendingRequests(
          user.id
        );
        setPendingRequests({
          total: response.total_pending_requests,
          oldTotal: response.total_pending_requests_old,
        });
      } catch (error) {
        console.error("Error fetching pending requests:", error);
        // Optionally set some error state here
      } finally {
        setIsPendingRequestsLoading(false);
      }
    };

    fetchPendingRequests();
  }, [reload, user?.id]);

  // Update the total members useEffect
  useEffect(() => {
    const fetchTotalMembers = async () => {
      if (!communityId) {
        setIsTotalMembersLoading(true);
        return;
      }

      try {
        setIsTotalMembersLoading(true);
        const response = await dashboardService.countCommunityUsers(communityId);
        // console.log("Total members response:", response);
        setTotalMembers({
          total: response[0].total_members,
          oldTotal: response[0].total_members_old,
        });
        // console.log("Total members state:", totalMembers);
      } catch (error) {
        console.error("Error fetching total members:", error);
        // Optionally set some error state here
      } finally {
        setIsTotalMembersLoading(false);
      }
    };

    fetchTotalMembers();
  }, [reload, communityId]);



  useEffect(() => {
    const fetchCommunityName = async () => {
      try {
        // Skip if we already have the name in location state
        if (location.state?.communityName) {
          setCommunityName(location.state.communityName);
          return;
        }

        const response = await communityService.getCommunityDetails(
          communityId
        );

        if (response?.Data?.length > 0) {
          const name = response.Data[0].name;
          setCommunityName(name);
          // Update navigation state while preserving any existing state
          navigate(location.pathname, {
            ...location,
            state: {
              ...location.state,
              communityName: name,
            },
            replace: true,
          });
          // console.log("Fetched community name:", name);
          // console.log("Current location state:", location.state);
        }
      } catch (error) {
        console.error("Error fetching community name:", error);
      }
    };

    if (communityId) {
      fetchCommunityName();
    }
  }, [communityId, navigate, location]);



  // ========================================================
  // 2. Zustand store and page hydration
  // ========================================================
  const { isLoading: isLoadingMemberships, fetchItems, getSortedFiltered } = useItems(
    (state) => state
  );
  // console.log("communityId: ", communityId);
  const data = getSortedFiltered((membership) =>
    membership.communityId === communityId
  );
  // console.log("data: ", data);
  const showLoading = data.length === 0 && isLoadingMemberships;
  // console.log(`${storeName}:`, data);
  useEffect(() => {
    fetchItems({}, [communityId]);
  }, [reload, communityId]);
  // ========================================================

  return (
    <div className="flex flex-col md:flex-row min-h-screen ">
      <div className="flex-1 flex flex-col">
        <div className="flex-1 p-4 sm:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Total Members - With Skeleton */}
            {isTotalMembersLoading ? (
              <Skeleton className="w-full h-32 rounded-xl" />
            ) : (
              <TableCard
                heading="Total Members"
                number={totalMembers.total}
                trend={{
                  percentage: `${Math.abs(
                    ((totalMembers.total - totalMembers.oldTotal) /
                      (totalMembers.oldTotal || 1)) *
                      100
                  ).toFixed(0)}%`,
                  isUp: totalMembers.total > totalMembers.oldTotal,
                }}
                bgColor="from-teal-50 to-teal-300"
                icon={GoPeople}
                iconColor="text-teal-400"
                borderColor="border-l-teal-400"
              />
            )}
            {/* Pending requests - With Skeleton */}
            {isPendingRequestsLoading ? (
              <Skeleton className="w-full h-32 rounded-xl" />
            ) : (
              <TableCard
                heading="Pending Requests"
                number={pendingRequests.total}
                trend={{
                  percentage: `${Math.abs(
                    ((pendingRequests.total - pendingRequests.oldTotal) /
                      (pendingRequests.oldTotal || 1)) *
                      100
                  ).toFixed(0)}%`,
                  isUp: pendingRequests.total > pendingRequests.oldTotal,
                }}
                bgColor="from-orange-50 to-orange-300"
                icon={GoLog}
                iconColor="text-yellow-400"
                buttonText="View More"
                buttonLink="/pending-requests"
                borderColor="border-l-yellow-400"
              />
            )}
          </div>

          <div className="bg-white shadow rounded-lg md:max-w-[calc(100vw-375px)] p-4 sm:p-6 text-sm mt-6">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
              <div className="text-base sm:text-lg font-semibold text-[#111827] mb-4 sm:mb-0">
                Community Member List
              </div>
              <div className="flex space-x-4">
                <Button
                  onClick={() => {
                    console.log("Opening Import Modal...");
                    setShowImportModal(true);
                  }}
                  className="p-2 bg-[#F9FAFB] border-[#E5EEB] rounded border-[1px] text-sm sm:text-base text-[#6B7280] hover:bg-[#F0FDFA] hover:text-[#14B8A6] flex items-center"
                >
                  <FaDownload className="mr-2 w-4" /> Import
                </Button>

                <Button
                  onClick={() => setShowAddModal(true)}
                  className="flex items-center px-4 py-2 bg-[#14B8A6] text-sm sm:text-base text-white rounded hover:bg-[#2DD4BF]"
                >
                  <FaPlus className="mr-2" /> Invite Member
                </Button>
              </div>
            </div>
            <hr />
            <div className="overflow-x-auto w-full max-w-screen-lg">
              {showLoading ? (
                <LoadingImage />
              ) : data.length > 0 ? (
                <DataTable
                  columns={columns}
                  data={data}
                  actions={actions}
                />
              ) : (
                <div className="text-center text-gray-500 py-6">
                  No members found.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Invite Modal - with Group Dropdown */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSelectedGroup(null); // Reset group selection when closing
        }}
        title="Invite Member"
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddModal(false);
                setSelectedGroup(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleInviteMember} disabled={loading}>
              {loading ? "Sending..." : "Send Invitation"}
            </Button>
          </div>
        }
      >
        <hr />
        <Label className="mt-4 text-left">Member Name:</Label>
        <Input
          placeholder="Enter member name"
          value={memberName}
          onChange={(e) => setMemberName(e.target.value)}
        />

        <Label className="mt-4 text-left">Member Email:</Label>
        <MultiEmailSelect
          selectedEmails={email}
          onChange={handleEmailChange}
          isDisabled={false}
          isMulti={false}
          className="text-left"
        />

        <Label className="mt-4 text-left">Membership Role:</Label>
        <Select
          className="mb-6"
          value={selectedRole}
          onValueChange={setSelectedRole}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="U">User</SelectItem>
            <SelectItem value="GL">Group Leader</SelectItem>
            <SelectItem value="CA">Community Admin</SelectItem>
          </SelectContent>
        </Select>

        {/* Conditionally show group dropdown if groups exist */}
        {getGroups.data?.length > 0 && (
          <>
            <Label className="mt-4 text-left">Assign to Group:</Label>
            <Select
              className="mb-6"
              value={selectedGroup}
              onValueChange={setSelectedGroup}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Group" />
              </SelectTrigger>
              <SelectContent>
                {getGroups.data.map((group) => (
                  <SelectItem key={group.groupId} value={group.groupId}>
                    {group.groupName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}

        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
      </Modal>

      {/* Import Modal - done */}
      <Modal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Users"
        description="Upload a file to import users into the system."
        icon={<FaDownload className="text-green-500" />}
        iconColor="green"
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowImportModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleFileImport} disabled={loading}>
              {loading ? "Importing..." : "Import"}
            </Button>
          </div>
        }
      >
        <Label>Upload File:</Label>
        <input
          type="file"
          className="w-full border border-gray-300 p-2 rounded-lg"
          onChange={(e) => setFile(e.target.files[0])}
        />

        <div className="mt-2 text-sm text-gray-500">
          <p>CSV should include columns: Email, Name, Role, Group</p>
          <p className="mt-1">
            Available groups:{" "}
            {getGroups.data?.map((g) => g.groupName).join(", ")}
          </p>
        </div>
      </Modal>

      {/* View Modal - done */}
      {showViewModal && selectedMember && (
        <Modal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          title="View Member Details"
          description="Below are the details of the selected member."
        >
          {/* Member ID, Date Created, Date Updated */}
          <div className="bg-green-100 p-4 rounded-lg mb-4 grid grid-cols-2 md:grid-cols-3 gap-4">
            {/* Member ID */}
            <div>
              <p className="text-sm font-semibold text-gray-700">Membership ID</p>
              <p className="text-base text-gray-900">
                <ShortenedText text={selectedMember.membershipId} />
              </p>
            </div>

            {/* Date Created */}
            <div>
              <p className="text-sm font-semibold text-gray-700">
                Date Created
              </p>
              <p className="text-base text-gray-900">
                {new Date(selectedMember.createDate).toLocaleString().split(",")[0]}
              </p>
            </div>

            {/* Date Updated */}
            <div className="col-span-2 md:col-span-1">
              <p className="text-sm font-semibold text-gray-700">
                Date Updated
              </p>
              <p className="text-base text-gray-900">
                {new Date(selectedMember.lastUpdate).toLocaleString().split(",")[0]}
              </p>
            </div>
          </div>

          <div className="text-left">
            {[
              { label: "Status", value: selectedMember.status },
              { label: "User Name", value: selectedMember.entityName },
              { label: "User Email", value: selectedMember.email },
              {
                label: "Designation",
                value: roleMapping[selectedMember.role] || selectedMember.role,
              },
              { label: "Group", value: selectedMember.groupName },
              {
                label: "Profile",
                value: (
                  <ProfileCard
                    name={selectedMember.profileName}
                    profileId={<ShortenedText text={selectedMember.profileId} />}
                    dateCreated={new Date(selectedMember.profileCreateDate).toLocaleString().split(",")[0]}
                    variant="teal"
                  />
                ),
              },
            ].map((item, index) => (
              <div
                key={item.label}
                className={`flex items-center flex-wrap px-4 py-2 ${
                  index % 2 === 0 ? "bg-gray-100" : "bg-white"
                }`}
              >
                <p className="text-sm font-semibold text-gray-700 w-[120px] min-w-[100px]">
                  {item.label}
                </p>

                <div className="text-base text-gray-900 flex-1 break-words">
                  {item.label === "Status" ? (
                    <div
                      className={`inline-block px-3 py-1 text-sm font-semibold rounded-full ${
                        item.value === "Active"
                          ? "text-[#16A34A] bg-[#DCFCE7]"
                          : item.value === "Suspended"
                          ? "text-[#EF4444] bg-[#FEE2E2]"
                          : "text-[#CA8A04] bg-[#FEF9C3]"
                      }`}
                    >
                      {item.value}
                    </div>
                  ) : (
                    item.value
                  )}
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* Edit Modal - done */}
      {showEditModal && selectedMember && (
        <Modal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          title="Edit Member Details"
          description="Below are the details of the selected member. You can edit the status here."
        >
          {/* Member ID, Date Created, Date Updated */}
          <div className="bg-green-100 p-4 rounded-lg mb-4 grid grid-cols-2 md:grid-cols-3 gap-4">
            {/* Member ID */}
            <div>
              <p className="text-sm font-semibold text-gray-700">Membership ID</p>
              <p className="text-base text-gray-900">
                <ShortenedText text={selectedMember.membershipId} />
              </p>
            </div>

            {/* Date Created */}
            <div>
              <p className="text-sm font-semibold text-gray-700">
                Date Created
              </p>
              <p className="text-base text-gray-900">
                {new Date(selectedMember.createDate).toLocaleString().split(",")[0]}
              </p>
            </div>

            {/* Date Updated */}
            <div className="col-span-2 md:col-span-1">
              <p className="text-sm font-semibold text-gray-700">
                Date Updated
              </p>
              <p className="text-base text-gray-900">
                {new Date(selectedMember.lastUpdate).toLocaleString().split(",")[0]}
              </p>
            </div>
          </div>

          {/* Status, Username, Email, Profile */}
          <div className="text-left">
            {[
              {
                label: "Status",
                value: (
                  <div className="flex space-x-0">
                    {["Active", "Suspended"].map((statusOption) => (
                      <button
                        key={statusOption}
                        onClick={() => handleStatusChange(statusOption)}
                        className={`w-full px-4 py-2 border font-semibold text-sm ${
                          selectedMember.status === statusOption
                            ? statusOption === "Suspended"
                              ? "bg-red-500 text-white border-red-500 hover:bg-red-600"
                              : "bg-green-500 text-white border-green-500 hover:bg-green-600"
                            : "bg-white text-black border-gray-300 hover:bg-gray-100"
                        }`}
                      >
                        {statusOption}
                      </button>
                    ))}
                  </div>
                ),
              },
              { label: "User Name", value: selectedMember.entityName },
              { label: "User Email", value: selectedMember.email },
              {
                label: "Profile",
                value: (
                  <ProfileCard
                    name={selectedMember.profileName}
                    profileId={<ShortenedText text={selectedMember.profileId} />}
                    dateCreated={new Date(selectedMember.profileCreateDate).toLocaleString().split(",")[0]}
                    variant="teal"
                  />
                ),
              },
            ].map((item, index) => (
              <div
                key={item.label}
                className={`flex items-center flex-wrap px-4 py-2 ${
                  index % 2 === 0 ? "bg-gray-100" : "bg-white"
                }`}
              >
                <p className="text-sm font-semibold text-gray-700 w-[120px] min-w-[100px]">
                  {item.label}
                </p>
                <div className="text-base text-gray-900 flex-1 break-words">
                  {item.value}
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end mt-4 space-x-4">
            <Button
              variant="secondary"
              onClick={() => setShowEditModal(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveChanges} disabled={isLoading}>
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </Modal>
      )}


      {/* Activate user */}
      <Modal
        isOpen={isActivateOpen}
        onClose={() => setIsActivateOpen(false)}
        title="Activate Member"
        description="Are you sure you want to Activate this member?"
        icon={<FaCheckCircle className="text-green-500" />}
        iconColor="green"
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setIsActivateOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmActivate} disabled={isLoading}>
              {isLoading ? "Activating..." : "Activate"}
            </Button>
          </div>
        }
      ></Modal>

      {/* Suspend Modal - done */}
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Suspend Member"
        description="Are you sure you want to suspend this member?"
        icon={<FaExclamationTriangle className="text-red-500" />}
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant="secondary"
              onClick={() => setIsOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmSuspend}
              disabled={isLoading}
            >
              {isLoading ? "Suspending..." : "Suspend"}
            </Button>
          </div>
        }
      ></Modal>

      {/* Delete modal - done*/}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Remove Member"
        description="Are you sure you want to remove this member? This action is NOT reversible and will result in permanent loss of this member."
        icon={<FaExclamationTriangle className="text-red-500" />}
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowDeleteModal(false)}
            >
              No, Cancel
            </Button>
            <Button
              variant={"destructive"}
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "Removing..." : "Yes, remove this member"}
            </Button>
          </div>
        }
      />
    </div>
  );
};

export default MemberManagement;
